/**
 * Feature Gate Component
 *
 * Conditionally renders children based on feature toggle state.
 * Provides a declarative way to enable/disable features in the UI.
 *
 * Features:
 * - Simple toggle check with fallback support
 * - A/B test variant rendering
 * - Loading state handling
 * - Inverse logic support (render when disabled)
 *
 * Usage:
 * ```tsx
 * // Basic feature gate
 * <FeatureGate toggleId="pro_calculator">
 *   <ProCalculator />
 * </FeatureGate>
 *
 * // With fallback
 * <FeatureGate toggleId="new_results_ui" fallback={<OldResultsUI />}>
 *   <NewResultsUI />
 * </FeatureGate>
 *
 * // Show while loading
 * <FeatureGate toggleId="feature" showWhileLoading>
 *   <Component />
 * </FeatureGate>
 *
 * // Inverse logic (render when toggle is OFF)
 * <FeatureGate toggleId="maintenance_mode" inverse>
 *   <NormalContent />
 * </FeatureGate>
 * ```
 *
 * @module frontend/src/components/FeatureGate
 */

/* eslint-disable react-refresh/only-export-components */

import type { ReactNode } from 'react';
import { Skeleton, Stack } from '@mantine/core';
import { useFeatureToggles } from '../contexts/FeatureToggleContext';

// ========================================
// TYPES
// ========================================

interface FeatureGateProps {
  /**
   * The feature toggle ID to check
   */
  toggleId: string;

  /**
   * Content to render when toggle is enabled (or disabled if inverse=true)
   */
  children: ReactNode;

  /**
   * Content to render when toggle is disabled (or enabled if inverse=true)
   * If not provided, nothing is rendered when the gate is closed
   */
  fallback?: ReactNode;

  /**
   * Default value if toggle not found (default: false)
   */
  defaultValue?: boolean;

  /**
   * If true, renders children while toggles are loading (default: false)
   * Useful for critical UI elements that shouldn't flash on load
   */
  showWhileLoading?: boolean;

  /**
   * If true, renders children when toggle is OFF instead of ON (default: false)
   * Useful for hiding content during maintenance mode
   */
  inverse?: boolean;

  /**
   * Custom loading component (default: Skeleton)
   */
  loadingComponent?: ReactNode;
}

/**
 * Feature Gate Component
 *
 * Conditionally renders content based on feature toggle state.
 * Provides declarative feature flag control for React components.
 */
export function FeatureGate({
  toggleId,
  children,
  fallback = null,
  defaultValue = false,
  showWhileLoading = false,
  inverse = false,
  loadingComponent,
}: FeatureGateProps) {
  const { isEnabled, loading } = useFeatureToggles();

  // Handle loading state
  if (loading && !showWhileLoading) {
    // Return custom loading component or default skeleton
    if (loadingComponent !== undefined) {
      return <>{loadingComponent}</>;
    }
    // Default: return null (render nothing while loading)
    return null;
  }

  // Get toggle state
  const enabled = isEnabled(toggleId, defaultValue);

  // Apply inverse logic if needed
  const shouldRender = inverse ? !enabled : enabled;

  // Render based on toggle state
  if (shouldRender) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
}

// ========================================
// VARIANT GATE (A/B Testing)
// ========================================

interface VariantGateProps {
  /**
   * The experiment toggle ID
   */
  toggleId: string;

  /**
   * Map of variant ID to component to render
   */
  variants: Record<string, ReactNode>;

  /**
   * Content to render if user is not in the experiment or toggle is disabled
   */
  fallback?: ReactNode;

  /**
   * If true, renders fallback while toggles are loading (default: false)
   */
  showWhileLoading?: boolean;
}

/**
 * Variant Gate Component
 *
 * Renders different content based on A/B test variant assignment.
 * Used for experiment toggles with multiple variants.
 *
 * Usage:
 * ```tsx
 * <VariantGate
 *   toggleId="checkout_experiment"
 *   variants={{
 *     control: <CheckoutA />,
 *     variant_b: <CheckoutB />,
 *     variant_c: <CheckoutC />,
 *   }}
 *   fallback={<DefaultCheckout />}
 * />
 * ```
 */
export function VariantGate({
  toggleId,
  variants,
  fallback = null,
  showWhileLoading = false,
}: VariantGateProps) {
  const { isEnabled, getVariant, loading } = useFeatureToggles();

  // Handle loading state
  if (loading && !showWhileLoading) {
    return null;
  }

  // Check if toggle is enabled
  if (!isEnabled(toggleId, false)) {
    return <>{fallback}</>;
  }

  // Get user's variant
  const variant = getVariant(toggleId);

  // Render variant component or fallback
  if (variant && variants[variant]) {
    return <>{variants[variant]}</>;
  }

  // No variant or variant not found - use fallback
  return <>{fallback}</>;
}

// ========================================
// FEATURE SKELETON (Loading Placeholder)
// ========================================

interface FeatureSkeletonProps {
  /**
   * Height of the skeleton in pixels or CSS value
   */
  height?: number | string;

  /**
   * Width of the skeleton (default: 100%)
   */
  width?: number | string;

  /**
   * Number of skeleton lines (default: 1)
   */
  lines?: number;

  /**
   * Gap between skeleton lines (default: sm)
   */
  gap?: 'xs' | 'sm' | 'md' | 'lg';
}

/**
 * Feature Skeleton Component
 *
 * Loading placeholder for feature gates.
 * Use as loadingComponent prop in FeatureGate.
 *
 * Usage:
 * ```tsx
 * <FeatureGate
 *   toggleId="dashboard_widget"
 *   loadingComponent={<FeatureSkeleton height={200} lines={3} />}
 * >
 *   <DashboardWidget />
 * </FeatureGate>
 * ```
 */
export function FeatureSkeleton({
  height = 40,
  width = '100%',
  lines = 1,
  gap = 'sm',
}: FeatureSkeletonProps) {
  if (lines === 1) {
    return <Skeleton height={height} width={width} animate />;
  }

  return (
    <Stack gap={gap}>
      {Array.from({ length: lines }).map((_, index) => (
        <Skeleton key={index} height={height} width={width} animate />
      ))}
    </Stack>
  );
}

// ========================================
// HIGHER-ORDER COMPONENT
// ========================================

/**
 * Higher-order component to wrap any component with a FeatureGate
 *
 * Usage:
 * ```tsx
 * const GatedProCalculator = withFeatureGate(ProCalculator, 'pro_calculator');
 *
 * // Then use anywhere:
 * <GatedProCalculator {...props} />
 * ```
 */
export function withFeatureGate<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  toggleId: string,
  options: Omit<FeatureGateProps, 'toggleId' | 'children'> = {}
) {
  return function WithFeatureGateComponent(props: P) {
    return (
      <FeatureGate toggleId={toggleId} {...options}>
        <WrappedComponent {...props} />
      </FeatureGate>
    );
  };
}

/**
 * Higher-order component to hide component during maintenance mode
 *
 * Usage:
 * ```tsx
 * const SafeComponent = hideOnMaintenance(MyComponent);
 * ```
 */
export function hideOnMaintenance<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  fallback: ReactNode = null
) {
  return function WithMaintenanceGateComponent(props: P) {
    return (
      <FeatureGate toggleId="maintenance_mode" inverse fallback={fallback}>
        <WrappedComponent {...props} />
      </FeatureGate>
    );
  };
}
